import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { ArrowUpRight, TrendingUp, Users, FolderKanban, IndianRupee, AlertTriangle, Wallet } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const KPI = ({ label, value, sub, icon: Icon, tone, testId }) => (
  <div className="bg-white border border-anvi-200 rounded-md p-5 flex flex-col gap-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tone}`}>
        <Icon size={16} strokeWidth={1.5} />
      </div>
    </div>
    <div className="font-heading text-3xl text-anvi-900 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-anvi-500">{sub}</div>}
  </div>
);

const STATUS_LABELS = {
  new: { label: "New", tone: "bg-anvi-100 text-anvi-700" },
  contacted: { label: "Contacted", tone: "bg-blue-50 text-blue-700" },
  qualified: { label: "Qualified", tone: "bg-amber-50 text-amber-800" },
  proposal_sent: { label: "Proposal Sent", tone: "bg-purple-50 text-purple-800" },
  won: { label: "Won", tone: "bg-emerald-50 text-emerald-800" },
  lost: { label: "Lost", tone: "bg-red-50 text-red-700" },
};

export default function Dashboard() {
  useDocumentTitle("Dashboard");
  const { tenant } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.dashboard();
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-10 text-anvi-500">Loading…</div>;
  }

  const collectionRate = data.kpis.total_invoiced ? Math.round((data.kpis.collected / data.kpis.total_invoiced) * 100) : 0;

  return (
    <div className="px-10 pt-4 pb-10 max-w-[1400px] animate-fade-in" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">{tenant?.name || "Anvi Signature Spaces"} · Command Center</div>
          <h1 className="font-heading text-4xl text-anvi-900">Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}</h1>
          <p className="text-sm text-anvi-500 mt-2">Your firm at a glance</p>
        </div>
        <div className="text-sm text-anvi-500 tabular-nums">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <KPI label="Total Leads" value={data.kpis.total_leads} sub="All-time captured" icon={Users} tone="bg-anvi-100 text-anvi-700" testId="kpi-total-leads" />
        <KPI label="Active Projects" value={data.kpis.active_projects} sub="Design + execution" icon={FolderKanban} tone="bg-anvi-100 text-anvi-700" testId="kpi-active-projects" />
        <KPI label="Invoiced" value={inrShort(data.kpis.total_invoiced)} sub="Fiscal YTD" icon={IndianRupee} tone="bg-anvi-terracotta/10 text-anvi-terracotta" testId="kpi-total-invoiced" />
        <KPI label="Collected" value={inrShort(data.kpis.collected)} sub={`${collectionRate}% collection rate`} icon={Wallet} tone="bg-emerald-50 text-emerald-700" testId="kpi-collected" />
        <KPI label="Overdue" value={inrShort(data.kpis.overdue)} sub="Needs follow-up" icon={AlertTriangle} tone="bg-red-50 text-red-700" testId="kpi-overdue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 bg-white border border-anvi-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Revenue Trend</div>
              <div className="font-heading text-xl text-anvi-900 mt-1">Last 6 months</div>
            </div>
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <TrendingUp size={14} strokeWidth={1.5} />
              <span className="tabular-nums">+18% vs prev</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_revenue} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9C5245" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#9C5245" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E7E5E4" strokeDasharray="0" horizontal vertical={false} />
                <XAxis dataKey="month" stroke="#78716C" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#78716C" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E7E5E4", borderRadius: 6, fontSize: 12 }} formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="amount" stroke="#9C5245" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-anvi-200 rounded-md p-6" data-testid="recent-leads-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Recent Leads</div>
              <div className="font-heading text-xl text-anvi-900 mt-1">Latest 5</div>
            </div>
            <Link to="/leads" className="text-xs text-anvi-terracotta hover:underline flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="space-y-3">
            {data.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-anvi-100 pb-3 last:border-b-0" data-testid={`recent-lead-${l.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-anvi-900 truncate">{l.client_name}</div>
                  <div className="text-xs text-anvi-500 truncate">{l.source} · {l.property_type}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_LABELS[l.status]?.tone}`}>{STATUS_LABELS[l.status]?.label}</span>
                  <span className="text-xs font-medium tabular-nums text-anvi-700 w-8 text-right">{l.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md p-6" data-testid="active-projects-list">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Active Projects</div>
            <div className="font-heading text-xl text-anvi-900 mt-1">In design or execution</div>
          </div>
          <Link to="/projects" className="text-xs text-anvi-terracotta hover:underline flex items-center gap-1">
            View all <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.active_projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="border border-anvi-200 hover:border-anvi-terracotta rounded-md p-4 transition-colors group" data-testid={`project-card-${p.id}`}>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">{p.status}</div>
              <div className="font-heading text-lg text-anvi-900 mt-1 group-hover:text-anvi-terracotta transition-colors">{p.name}</div>
              <div className="text-sm text-anvi-500 mt-1">{p.client_name} · {p.location}</div>
              <div className="text-sm font-medium text-anvi-900 mt-3 tabular-nums">{inrShort(p.budget)}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
